/**
 * useSectionSelectionSync — synchronizacja tabeli sekcji kokpitu decyzji ze wspólnym zaznaczeniem
 * zakładki: zgłaszanie wczytanych kroków, zdejmowanie zaznaczenia sekcji przy zmianie danych lub
 * klucza zapytania i sprzątanie przy zwinięciu (pełne zasady → `DecisionSectionTable.tsx`,
 * „Zaznaczanie"; tłumaczenie rekord ↔ kroki → `selection.ts`).
 *
 * KOLEJNOŚĆ EFEKTÓW w haku jest częścią kontraktu (komentarze przy każdym). Hak należy wołać PO
 * zapytaniu sekcji i po wyliczeniu `rowsHidden` — a przed efektami, które w tabeli stoją
 * dalej. Zwraca uchwyty potrzebne tabeli: `onSliceChange` (podmiana wycinka zaznaczenia sekcji) i
 * `clearOwnSelection` (zdjęcie zaznaczenia sekcji przy zmianie strony / sortu / filtra z ręki
 * użytkownika) — dlatego uchwyty tych zmian tabela definiuje PO wywołaniu haka.
 */

import { useCallback, useEffect, useMemo, useRef } from 'react';

import type { MyPendingDecisionRow } from '@/lib/processes/approvals-api';

import {
  type DecisionSectionSelection,
  partiallyMarkedStepIds,
  type SelectableSectionRow,
} from './selection';

/** To, co hak czyta z wyniku zapytania sekcji (`useQuery` tabeli); przekazuj cały wynik. */
export interface SelectionSyncQuery {
  data: { items: readonly SelectableSectionRow[] } | undefined;
  isPlaceholderData: boolean;
}

export interface UseSectionSelectionSyncArgs {
  /** Klucz grupy sekcji — pod nim zakładka trzyma zgłoszone kroki. */
  groupKey: string;
  /**
   * Serializowany klucz zapytania sekcji (strona, sort, filtr, zakres) — jego zmiana zdejmuje
   * zaznaczenie sekcji.
   */
  sectionKeyId: string;
  /** Sekcja tylko do odczytu („Zespół") — niczego nie zgłasza zakładce i niczego nie zdejmuje. */
  readOnly: boolean;
  /** Udział we wspólnym zaznaczeniu; brak = sekcja bez zaznaczania. */
  selection: DecisionSectionSelection | undefined;
  /**
   * Wiersze NIE są pokazane w tabeli (puste): błąd stwierdzony dla bieżącego klucza zapytania albo
   * brak (poprawnego) katalogu kolumn kartoteki własnej — zamiast tabeli stoi wtedy „Ładowanie" lub
   * błąd sekcji. Z tej flagi i z `query.data.items` hak sam wylicza wiersze pokazane w tabeli —
   * jeden źródłowy zbiór dla zgłaszanych kroków i dla czyszczenia zaznaczenia częściowego.
   */
  rowsHidden: boolean;
  query: SelectionSyncQuery;
}

export interface SectionSelectionSync {
  /** Podmiana wycinka zaznaczenia tej sekcji; `undefined` = sekcja bez zaznaczania. */
  onSliceChange: DecisionSectionSelection['onSliceChange'] | undefined;
  /** Zdejmuje zaznaczenie kroków tej sekcji (zmiana strony, sortu lub filtra). */
  clearOwnSelection: () => void;
}

export function useSectionSelectionSync({
  groupKey,
  sectionKeyId,
  readOnly,
  selection,
  rowsHidden,
  query,
}: UseSectionSelectionSyncArgs): SectionSelectionSync {
  const markedStepIds = selection?.markedStepIds;
  const { data, isPlaceholderData } = query;

  // Wiersze pokazane w tabeli (puste, gdy ukryte) — z tych samych danych co `loadedSteps` niżej,
  // więc czyszczenie zaznaczenia częściowego i zgłaszane kroki nie mogą się rozjechać.
  const rows = useMemo<readonly SelectableSectionRow[]>(
    () => (rowsHidden ? [] : (data?.items ?? [])),
    [rowsHidden, data],
  );

  // Id kroków, które ta sekcja ma TERAZ wczytane i pokazane (uzupełnia je efekt zgłaszający
  // kroki zakładce, niżej) — z nich składa się „zaznaczenie tylko tej sekcji", które zdejmuje
  // zmiana strony, filtra lub sortu. Ref, żeby uchwyty zdarzeń zostały stabilne. Sekcja tylko do
  // odczytu nie ma zaznaczenia, więc nie dostaje też uchwytu jego zmiany.
  const ownStepIdsRef = useRef<readonly string[]>([]);
  const onSliceChange = readOnly ? undefined : selection?.onSliceChange;
  const clearOwnSelection = useCallback(() => {
    if (onSliceChange !== undefined && ownStepIdsRef.current.length > 0) {
      onSliceChange(ownStepIdsRef.current, []);
    }
  }, [onSliceChange]);

  // Kroki, które sekcja ma wczytane i POKAZANE — zgłaszane zakładce (licznik, akcja masowa, pauza
  // auto-odświeżania). `null` = brak: wiersze niepokazane (błąd, brak katalogu kolumn) i dane
  // zastępcze (wiersze z poprzedniego klucza zapytania, których nie wolno ani zaznaczać, ani
  // liczyć), a po zwinięciu sekcji — odmontowanie (efekt niżej).
  const loadedSteps = useMemo<MyPendingDecisionRow[] | null>(
    () =>
      rowsHidden || isPlaceholderData
        ? null
        : (data?.items.flatMap((row) => row.decisions) ?? null),
    [rowsHidden, isPlaceholderData, data],
  );
  // Sekcja tylko do odczytu („Zespół") nie bierze udziału w zaznaczeniu: niczego nie zgłasza
  // zakładce.
  const reportLoadedSteps = readOnly ? undefined : selection?.onLoadedStepsChange;

  // Aktualne zaznaczenie i uchwyty zakładki (zmiana zaznaczenia, zgłaszanie kroków) dla efektów,
  // które NIE mają się uruchamiać przy ich zmianie (zdjęcie zaznaczenia po zmianie danych i
  // sprzątanie przy zwinięciu) — czytają je z refów.
  const markedStepIdsRef = useRef(markedStepIds);
  const onSliceChangeRef = useRef(onSliceChange);
  const reportLoadedStepsRef = useRef(reportLoadedSteps);
  useEffect(() => {
    markedStepIdsRef.current = markedStepIds;
    onSliceChangeRef.current = onSliceChange;
    reportLoadedStepsRef.current = reportLoadedSteps;
  }, [markedStepIds, onSliceChange, reportLoadedSteps]);

  // Klucz zapytania zmienia się razem ze zbiorem wierszy pod zaznaczeniem (strona, sort, filtr,
  // zakres). Uchwyty strony / sortu / filtra zdejmują zaznaczenie od razu, ale filtr „Szukaj…"
  // trafia do zapytania z opóźnieniem (`useDebouncedValue`): zaznaczenie zrobione w tym oknie
  // przeżyłoby zmianę filtra, więc zdejmujemy je też tutaj. Efekt MUSI stać przed efektem
  // zgłaszającym kroki (niżej): ten drugi nadpisuje `ownStepIdsRef` (na danych zastępczych —
  // pustą listą), a tu potrzebne są kroki sprzed zmiany.
  const lastSectionKeyRef = useRef(sectionKeyId);
  useEffect(() => {
    if (lastSectionKeyRef.current === sectionKeyId) return;
    lastSectionKeyRef.current = sectionKeyId;
    clearOwnSelection();
  }, [sectionKeyId, clearOwnSelection]);

  useEffect(() => {
    if (reportLoadedSteps === undefined) return;
    ownStepIdsRef.current = loadedSteps?.map((step) => step.stepInstanceId) ?? [];
    reportLoadedSteps(groupKey, loadedSteps);
  }, [groupKey, loadedSteps, reportLoadedSteps]);

  // Po KAŻDEJ zmianie wczytanych danych rekordy zaznaczone tylko częściowo tracą zaznaczenie
  // kroków (patrz opis pliku tabeli i `partiallyMarkedStepIds`). Zaznaczenie czytane z refa, nie
  // z zależności: sama jego zmiana (np. decyzja zdjęła jeden krok rekordu, a odświeżone wiersze
  // jeszcze nie dotarły) nie może uruchamiać czyszczenia, bo zdjęłaby krok, który po odświeżeniu
  // zostaje jedynym krokiem rekordu. Znany przypadek brzegowy: po decyzji z okna nieudane
  // odświeżenie (np. błąd sieci) nie przynosi nowych danych, więc efekt się nie uruchamia, a
  // rekord zaznaczony częściowo (licznik liczy jego pozostały krok) zostaje taki do najbliższej
  // zmiany danych — auto-odświeżanie jest wstrzymane przy zaznaczeniu, więc taką zmianę wywoła
  // dopiero ręczna akcja (ponowienie, zmiana strony itp.).
  useEffect(() => {
    const marked = markedStepIdsRef.current;
    if (onSliceChange === undefined || marked === undefined || loadedSteps === null) return;
    const partial = partiallyMarkedStepIds(rows, marked);
    if (partial.length > 0) onSliceChange(partial, []);
  }, [rows, loadedSteps, onSliceChange]);

  // Wypisanie z rejestru zakładki przy zwinięciu sekcji (odmontowaniu), błędzie zakresu itp.
  // Zwinięcie zdejmuje też zaznaczenie sekcji: po ponownym rozwinięciu startuje ona od pierwszej
  // strony, bez filtrów i sortowania, więc zaznaczenie sprzed zwinięcia dotyczyłoby innego zbioru
  // wierszy. Wyjście z trybu sekcji lub zmiana zakresu odmontowują sekcję po tym, jak zakładka
  // wyczyściła zaznaczenie — zdjęcie jest wtedy bez efektu (nic do zdjęcia), a nie błędem ani
  // drugim czyszczeniem. Uchwyty czytane z refów, a efekt zależy tylko od klucza sekcji:
  // zaznaczenie ma znikać wyłącznie przy prawdziwym odmontowaniu lub zmianie klucza sekcji — nowa
  // referencja wywołania zwrotnego od zakładki (np. po jego przeliczeniu) nie może uruchomić
  // sprzątania. Sekcja tylko do odczytu nie ma uchwytu (ref pusty), więc niczego nie wypisuje ani
  // nie zdejmuje (zakres zmienia klucz `key={scope}` w rodzicu).
  useEffect(
    () => () => {
      const report = reportLoadedStepsRef.current;
      if (report === undefined) return;
      const own = ownStepIdsRef.current;
      if (own.length > 0) onSliceChangeRef.current?.(own, []);
      report(groupKey, null);
    },
    [groupKey],
  );

  return { onSliceChange, clearOwnSelection };
}
